/**
 * @fileoverview Reusable date range popover with canonical click behavior.
 * @module shared/components/ui/RangePickerPopover
 */

import { ReactElement, useState } from 'react'
import { CalendarDaysIcon } from 'lucide-react'
import { type DateRange } from 'react-day-picker'
import { useTranslation } from 'react-i18next'
import { Button } from '@/shared/components/ui'
import { Calendar as CalendarComponent } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { formatDateRangeHuman, formatDateHuman } from '@/shared/lib/utils'
import { pl, enUS } from 'date-fns/locale'

export interface RangePickerPopoverProps {
  className?: string
  /** Initial selection to show when opening */
  value?: { from?: Date | undefined; to?: Date | undefined }
  /** Called when user completes a range (second click). Receives [start, end] inclusive. */
  onChange: (from: Date, to: Date) => void
  /** Align popover content relative to trigger. */
  align?: 'start' | 'center' | 'end'
}

/**
 * Canonical date range picker popover used across the app.
 * Implements the same click semantics as the FlyingMenu picker:
 * 1st click sets start; 2nd sets end (order corrected automatically);
 * 3rd click starts a new selection; identical dates yield a 1‑day range.
 */
export function RangePickerPopover({
  className = '',
  value,
  onChange,
  align = 'end',
}: RangePickerPopoverProps): ReactElement {
  const { i18n } = useTranslation('common')
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [tempRange, setTempRange] = useState<DateRange | undefined>(undefined)
  const [clickCount, setClickCount] = useState<number>(0)

  const locale = i18n.language === 'pl' ? pl : enUS

  const displayRange: DateRange | undefined =
    value?.from && value?.to ? { from: value.from, to: value.to } : undefined

  const handleOpenChange = (open: boolean): void => {
    setIsOpen(open)
    if (open) {
      setTempRange(displayRange)
      setClickCount(0)
    }
  }

  const handleDayClick = (day: Date): void => {
    setClickCount(prev => prev + 1)
    if (clickCount === 0) {
      setTempRange({ from: day, to: undefined })
      return
    }
    if (clickCount === 1 && tempRange?.from) {
      const first = tempRange.from
      const second = day
      const from = second.getTime() < first.getTime() ? second : first
      const to = second.getTime() < first.getTime() ? first : second
      setTempRange({ from, to })
      const start = new Date(from)
      start.setHours(0, 0, 0, 0)
      const end = new Date(to)
      end.setHours(23, 59, 59, 999)
      onChange(start, end)
      setIsOpen(false)
      setClickCount(0)
      return
    }
    setTempRange({ from: day, to: undefined })
    setClickCount(1)
  }

  const getDisplayText = (): string => {
    if (!displayRange?.from) {
      const today = new Date()
      const from = new Date(today.getFullYear(), today.getMonth(), 1)
      return formatDateRangeHuman(from, today, i18n.language)
    }
    if (!displayRange.to) return formatDateHuman(displayRange.from, i18n.language)
    return formatDateRangeHuman(displayRange.from, displayRange.to, i18n.language)
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`glass-button !h-10 !px-2 md:!px-3 lg:!px-4 py-2 flex items-center md:space-x-1.5 lg:space-x-2 justify-start text-left font-normal ${className}`}
        >
          <CalendarDaysIcon className="w-4 h-4 text-muted-foreground" />
          <span className="text-muted-foreground hidden md:inline text-xs lg:text-sm">{getDisplayText()}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="glass-card w-auto p-0" align={align}>
        <CalendarComponent
          mode="range"
          selected={tempRange}
          onDayClick={handleDayClick}
          numberOfMonths={2}
          locale={locale}
          {...(displayRange?.from && { defaultMonth: displayRange.from })}
          disabled={date => date > new Date() || date < new Date('1900-01-01')}
          className="rounded-lg border-0"
        />
      </PopoverContent>
    </Popover>
  )
}

export default RangePickerPopover
